import './CopHomePageCount.scss'

import { useEffect, useRef } from 'react'

import useCopHomePageCount from './useCopHomePageCount'

const KPI_VISIBLE_CLASS = 'cop-kpi-visible'
const KPI_VISIBLE_BODY_CLASS = 'cop-kpi-visible'

// Maps the authored cardType to the CSS scope used for the (unrelated) page tab ids,
// so each section's tab can show/hide independently of sibling cop-card instances.
const SECTION_CLASS_SUFFIX: Record<string, string> = {
  KPIsSection: 'performance-overview',
  CompanyCasesKPIsSection: 'all-cases',
}

const BriefcaseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="cop-count-card__svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
  </svg>
)

const FolderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="cop-count-card__svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    className="cop-count-card__svg"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

export type AuthoredCardItem = {
  label?: string
  dataKey?: string
}

export type CopHomePageCountProps = {
  readonly counttype?: string
  readonly items?: string | AuthoredCardItem[]
}

const CARD_ICONS = [BriefcaseIcon, FolderIcon, CheckCircleIcon]

const INDICATOR_LABELS: Record<string, string> = {
  CompanyCases: 'Company  Cases',
  CompanyOpenCases: 'Company Open Cases',
  CompanyMyActionCases: 'Company My Action Cases',
  CompanyResolvedCases: 'Company Resolved Cases',
  UserCases: 'My Cases',
  UserOpenCases: 'My Open Cases',
  UserMyActionCases: 'My Action Cases',
  UserResolvedCases: 'My Resolved Cases',
}

const formatLabel = (str: string): string => {
  return str.replace(/([A-Z])/g, " $1").trim();
};

function parseAuthoredItems(rawItems?: string | AuthoredCardItem[]): AuthoredCardItem[] {
  if (!rawItems) {
    return []
  }

  if (Array.isArray(rawItems)) {
    return rawItems
  }

  try {
    const parsed = JSON.parse(rawItems)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function buildLabelsByDataKey(items: AuthoredCardItem[]): Record<string, string> {
  return items.reduce<Record<string, string>>((acc, item) => {
    const dataKey = item.dataKey?.trim()
    const label = item.label?.trim()
    if (dataKey && label) {
      acc[dataKey] = label
    }
    return acc
  }, {})
}

const getDisplayLabel = (indicator: string, authoredLabels: Record<string, string>): string => {
  if (authoredLabels[indicator]) {
    return authoredLabels[indicator]
  }
  return INDICATOR_LABELS[indicator] || formatLabel(indicator)
}

export default function CopHomePageCount({ counttype, items }: CopHomePageCountProps) {
  const { currentSection, isLoading } = useCopHomePageCount(counttype)
  const authoredLabels = buildLabelsByDataKey(parseAuthoredItems(items))
  const rootRef = useRef<HTMLDivElement>(null)

  const kpiWidgets = currentSection?.widgets.filter(w => (w.type || '').trim().toLowerCase() === 'kpi') ?? []

  // AEM tabs/title are hidden by CSS by default; reveal them only when the API confirms KPI data.
  useEffect(() => {
    if (isLoading) return
    const shouldShow = kpiWidgets.length > 0

    // Toggle the class on THIS instance's own wrapper (not document.body) so a page with
    // multiple cop-card components doesn't have one instance hide/show another's title.
    const wrapperEl = rootRef.current?.closest('.cop-homepage-count-wrapper')
    wrapperEl?.classList.toggle(KPI_VISIBLE_CLASS, shouldShow)

    // The tab-navigation ids live elsewhere on the page (outside this wrapper), so they still
    // need a body-level class, scoped per section so sibling sections don't affect each other.
    const sectionClass = counttype ? `${KPI_VISIBLE_BODY_CLASS}--${SECTION_CLASS_SUFFIX[counttype] ?? counttype}` : KPI_VISIBLE_BODY_CLASS
    document.body.classList.toggle(sectionClass, shouldShow)

    return () => {
      wrapperEl?.classList.remove(KPI_VISIBLE_CLASS)
      document.body.classList.remove(sectionClass)
    }
  }, [isLoading, kpiWidgets.length, counttype])

  return (
    <div className="cop-home-page-count" ref={rootRef}>
      {kpiWidgets
        .map((widget, index) => {
          const Icon = CARD_ICONS[index % CARD_ICONS.length]
          return (
            <div key={widget.indicator} className="cop-count-card">
              <div className="cop-count-card__icon-wrapper">
                <Icon />
              </div>
              <div className="cop-count-card__body">
                <div className="cop-count-card__count">{widget.data.value}</div>
                <div className="cop-count-card__label">{getDisplayLabel(widget.indicator, authoredLabels)}</div>
              </div>
            </div>
          )
        })}
    </div>
  )
}
